// src/api/categorias.js - API para manejo de categorías
import { apiFetch } from './index.js';

/**
 * Obtener todas las categorías
 * @param {string} estado - 'all', 'activas', 'inactivas', 'activo', 'inactivo'
 * @returns {Promise} Lista de categorías
 */
export const obtenerCategorias = async (estado = 'all') => {
  try {
    console.log('📋 Obteniendo categorías...', { estado });
    
    let endpoint = '/Categoria';
    
    // Usar endpoint específico según el estado
    if (estado === 'activas') {
      endpoint = '/Categoria/activas';
    } else if (estado === 'inactivas' || estado === 'activo' || estado === 'inactivo') {
      endpoint = `/Categoria/estado/${estado}`;
    }
    
    const response = await apiFetch(endpoint);
    
    // Normalizar los datos para el frontend
    const data = Array.isArray(response) ? response : (response.data || []);
    const categoriasNormalizadas = data.map(cat => ({
      idCategoria: cat.idCategoria,
      nombre: cat.nombre,
      descripcion: cat.descripcion,
      margen_ganancia_default: cat.margen_ganancia_default || 0,
      estado: cat.estado,
      fechaCreacion: cat.fecha_creacion || cat.fechaCreacion,
      fecha_creacion: cat.fecha_creacion,
      totalProductos: cat.totalProductos || 0
    }));
    
    return {
      success: true,
      data: categoriasNormalizadas,
      count: categoriasNormalizadas.length,
      message: 'Categorías obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo categorías:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener una categoría por ID
 * @param {number} id - ID de la categoría
 * @returns {Promise} Datos de la categoría
 */
export const obtenerCategoriaPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de categoría es requerido');
    }
    
    console.log(`🔍 Obteniendo categoría ID: ${id}`);
    
    const response = await apiFetch(`/Categoria/${id}`);
    
    const data = response.data || response;
    
    return {
      success: true,
      data: {
        idCategoria: data.idCategoria,
        nombre: data.nombre,
        descripcion: data.descripcion,
        margen_ganancia_default: data.margen_ganancia_default || 0,
        estado: data.estado,
        fechaCreacion: data.fecha_creacion,
        fecha_creacion: data.fecha_creacion
      },
      message: response.message || 'Categoría obtenida exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo categoría ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener categoría con relaciones
 * @param {number} id - ID de la categoría
 * @returns {Promise} Datos de la categoría con relaciones
 */
export const obtenerCategoriaConRelaciones = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de categoría es requerido');
    }
    
    console.log(`🔍 Obteniendo categoría con relaciones ID: ${id}`);
    
    const response = await apiFetch(`/Categoria/${id}/conRelaciones`);
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Categoría obtenida exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo categoría con relaciones ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Buscar categorías por término
 * @param {string} termino - Término de búsqueda
 * @returns {Promise} Lista de categorías que coinciden
 */
export const buscarCategoriasAPI = async (termino) => {
  try {
    if (!termino || termino.trim() === '') {
      throw new Error('Término de búsqueda es requerido');
    }
    
    console.log(`🔎 Buscando categorías: "${termino}"`);
    
    const response = await apiFetch('/Categoria/search', {
      method: 'POST',
      body: JSON.stringify({ termino: termino.trim() })
    });
    
    const data = response.data || response;
    
    return {
      success: true,
      data: Array.isArray(data) ? data : [],
      count: Array.isArray(data) ? data.length : 0,
      message: response.message || 'Búsqueda completada'
    };
    
  } catch (error) {
    console.error(`❌ Error buscando categorías "${termino}":`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Verificar si una categoría existe por nombre
 * @param {string} nombre - Nombre a verificar
 * @returns {Promise} Resultado de la verificación
 */
export const verificarCategoriaPorNombre = async (nombre) => {
  try {
    if (!nombre || nombre.trim() === '') {
      throw new Error('Nombre es requerido');
    }
    
    console.log(`🔎 Verificando categoría por nombre: "${nombre}"`);
    
    const response = await apiFetch(`/Categoria/nombre/${encodeURIComponent(nombre)}/exists`);
    
    return {
      success: true,
      existe: response.exists || response.data?.exists || false,
      message: response.message || (response.exists ? 'La categoría ya existe' : 'Categoría disponible')
    };
    
  } catch (error) {
    console.error(`❌ Error verificando categoría "${nombre}":`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Verificar si una categoría existe por ID
 * @param {number} id - ID de la categoría
 * @returns {Promise} Resultado de la verificación
 */
export const verificarCategoriaPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de categoría es requerido');
    }
    
    console.log(`🔎 Verificando categoría ID: ${id}`);
    
    const response = await apiFetch(`/Categoria/${id}/exists`);
    
    return {
      success: true,
      existe: response.exists || response.data?.exists || false,
      message: response.message || (response.exists ? 'La categoría existe' : 'Categoría no existe')
    };
    
  } catch (error) {
    console.error(`❌ Error verificando categoría ${id}:`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Obtener categorías por rango de margen
 * @param {number} min - Margen mínimo
 * @param {number} max - Margen máximo
 * @returns {Promise} Lista de categorías en el rango
 */
export const obtenerCategoriasPorMargen = async (min, max) => {
  try {
    console.log(`📊 Obteniendo categorías con margen entre ${min} y ${max}`);
    
    const response = await apiFetch(`/Categoria/margen/${min}/${max}`);
    
    const data = response.data || response;
    
    return {
      success: true,
      data: Array.isArray(data) ? data : [],
      count: Array.isArray(data) ? data.length : 0,
      message: response.message || 'Categorías obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo categorías por margen:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Crear una nueva categoría
 * @param {Object} categoriaData - Datos de la categoría
 * @returns {Promise} Categoría creada
 */
export const crearCategoria = async (categoriaData) => {
  try {
    // Validar datos requeridos
    if (!categoriaData.nombre || categoriaData.nombre.trim() === '') {
      throw new Error('El nombre de la categoría es requerido');
    }
    
    console.log('➕ Creando nueva categoría:', categoriaData);
    
    const response = await apiFetch('/Categoria', {
      method: 'POST',
      body: JSON.stringify({
        nombre: categoriaData.nombre.trim(),
        descripcion: categoriaData.descripcion || null,
        margen_ganancia_default: parseInt(categoriaData.margen_ganancia_default) || 0,
        estado: categoriaData.estado || 'activo'
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Categoría creada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando categoría:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar una categoría existente
 * @param {number} id - ID de la categoría
 * @param {Object} categoriaData - Datos a actualizar
 * @returns {Promise} Categoría actualizada
 */
export const actualizarCategoria = async (id, categoriaData) => {
  try {
    if (!id) {
      throw new Error('ID de categoría es requerido');
    }
    
    if (!categoriaData.nombre || categoriaData.nombre.trim() === '') {
      throw new Error('El nombre de la categoría es requerido');
    }
    
    console.log(`✏️ Actualizando categoría ID: ${id}`, categoriaData);
    
    const payload = {
      idCategoria: parseInt(id),
      nombre: categoriaData.nombre.trim(),
      descripcion: categoriaData.descripcion || null,
      margen_ganancia_default: parseInt(categoriaData.margen_ganancia_default) || 0,
      estado: categoriaData.estado || 'activo'
    };
    
    console.log('📦 Payload enviado a la API:', payload);
    
    const response = await apiFetch(`/Categoria/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Categoría actualizada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando categoría ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Eliminar una categoría
 * @param {number} id - ID de la categoría a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarCategoria = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de categoría es requerido');
    }
    
    console.log(`🗑️ Eliminando categoría ID: ${id}`);
    
    const response = await apiFetch(`/Categoria/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Categoría eliminada exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando categoría ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Cambiar estado de una categoría
 * @param {number} id - ID de la categoría
 * @param {string} estado - Nuevo estado
 * @returns {Promise} Resultado del cambio de estado
 */
export const cambiarEstadoCategoria = async (id, estado) => {
  try {
    if (!id || !estado) {
      throw new Error('ID y estado son requeridos');
    }
    
    console.log(`🔄 Cambiando estado de categoría ID: ${id} a ${estado}`);
    
    // Primero obtenemos la categoría actual para mantener los demás campos
    const categoriaActual = await obtenerCategoriaPorId(id);
    
    if (!categoriaActual.success || !categoriaActual.data) {
      throw new Error('No se pudo obtener la categoría actual');
    }
    
    // Luego actualizamos con todos los campos, solo cambiando el estado
    const payload = {
      idCategoria: parseInt(id),
      nombre: categoriaActual.data.nombre,
      descripcion: categoriaActual.data.descripcion,
      margen_ganancia_default: categoriaActual.data.margen_ganancia_default || 0,
      estado: estado
    };
    
    const response = await apiFetch(`/Categoria/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estado de categoría actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error cambiando estado de categoría ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Activar una categoría
 * @param {number} id - ID de la categoría
 * @returns {Promise} Resultado de la activación
 */
export const activarCategoria = async (id) => {
  return await cambiarEstadoCategoria(id, 'activo');
};

/**
 * Desactivar una categoría
 * @param {number} id - ID de la categoría
 * @returns {Promise} Resultado de la desactivación
 */
export const desactivarCategoria = async (id) => {
  return await cambiarEstadoCategoria(id, 'inactivo');
};

// ==================== FUNCIONES AUXILIARES (CLIENTE-SIDE) ====================

/**
 * Buscar categorías por término (cliente-side)
 * @param {Array} categorias - Lista de categorías
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Categorías filtradas
 */
export const buscarCategoriasLocal = (categorias, termino) => {
  if (!termino || termino.trim() === '') {
    return categorias;
  }
  
  const terminoLower = termino.toLowerCase();
  return categorias.filter(categoria => 
    categoria.nombre?.toLowerCase().includes(terminoLower) ||
    categoria.descripcion?.toLowerCase().includes(terminoLower) ||
    convertirEstadoATexto(categoria.estado)?.toLowerCase().includes(terminoLower)
  );
};

/**
 * Filtrar categorías por estado (cliente-side)
 * @param {Array} categorias - Lista de categorías
 * @param {string} estado - 'all', 'activas', 'inactivas'
 * @returns {Array} Categorías filtradas
 */
export const filtrarCategoriasPorEstadoLocal = (categorias, estado) => {
  if (estado === 'all') return categorias;
  if (estado === 'activas') return categorias.filter(c => c.estado?.toLowerCase() === 'activo');
  if (estado === 'inactivas') return categorias.filter(c => c.estado?.toLowerCase() === 'inactivo');
  return categorias;
};

/**
 * Obtener estadísticas de categorías
 * @param {Array} categorias - Lista de categorías
 * @returns {Object} Estadísticas
 */
export const obtenerEstadisticasCategorias = (categorias) => {
  const activas = categorias.filter(c => c.estado?.toLowerCase() === 'activo').length;
  const inactivas = categorias.filter(c => c.estado?.toLowerCase() === 'inactivo').length;
  
  // Categorías con descripción
  const conDescripcion = categorias.filter(c => c.descripcion && c.descripcion.trim() !== '').length;
  
  // Promedio de margen
  const categoriasConMargen = categorias.filter(c => c.margen_ganancia_default !== undefined);
  const promedioMargen = categoriasConMargen.length > 0 
    ? Math.round(categoriasConMargen.reduce((sum, c) => sum + (c.margen_ganancia_default || 0), 0) / categoriasConMargen.length)
    : 0;
  
  // Categorías recientes (últimos 7 días)
  const sieteDiasAtras = new Date();
  sieteDiasAtras.setDate(sieteDiasAtras.getDate() - 7);
  const recientes = categorias.filter(c => {
    try {
      const fechaCreacion = new Date(c.fecha_creacion || c.fechaCreacion || 0);
      return fechaCreacion >= sieteDiasAtras;
    } catch {
      return false;
    }
  }).length;
  
  return {
    total: categorias.length,
    activas,
    inactivas,
    porcentajeActivas: categorias.length > 0 ? Math.round((activas / categorias.length) * 100) : 0,
    porcentajeInactivas: categorias.length > 0 ? Math.round((inactivas / categorias.length) * 100) : 0,
    conDescripcion,
    porcentajeConDescripcion: categorias.length > 0 ? Math.round((conDescripcion / categorias.length) * 100) : 0,
    promedioMargen,
    recientes,
    porcentajeRecientes: categorias.length > 0 ? Math.round((recientes / categorias.length) * 100) : 0
  };
};

/**
 * Ordenar categorías por diferentes criterios
 * @param {Array} categorias - Lista de categorías
 * @param {string} criterio - Criterio de ordenamiento
 * @returns {Array} Categorías ordenadas
 */
export const ordenarCategorias = (categorias, criterio = 'nombre') => {
  const categoriasCopy = [...categorias];
  
  switch (criterio) {
    case 'nombre':
      return categoriasCopy.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    case 'nombre_desc':
      return categoriasCopy.sort((a, b) => (b.nombre || '').localeCompare(a.nombre || ''));
    case 'fechaCreacion_desc':
      return categoriasCopy.sort((a, b) => new Date(b.fecha_creacion || b.fechaCreacion || 0) - new Date(a.fecha_creacion || a.fechaCreacion || 0));
    case 'fechaCreacion':
      return categoriasCopy.sort((a, b) => new Date(a.fecha_creacion || a.fechaCreacion || 0) - new Date(b.fecha_creacion || b.fechaCreacion || 0));
    case 'estado':
      return categoriasCopy.sort((a, b) => (a.estado || '').localeCompare(b.estado || ''));
    case 'margen_desc':
      return categoriasCopy.sort((a, b) => (b.margen_ganancia_default || 0) - (a.margen_ganancia_default || 0));
    case 'margen':
      return categoriasCopy.sort((a, b) => (a.margen_ganancia_default || 0) - (b.margen_ganancia_default || 0));
    default:
      return categoriasCopy;
  }
};

/**
 * Formatear fecha de categoría
 * @param {string} fechaISO - Fecha en formato ISO
 * @param {boolean} completo - Si incluir hora completa
 * @returns {string} Fecha formateada
 */
export const formatearFechaCategoria = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
    }
    
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Convertir estado a texto legible
 * @param {string} estado - Estado de la categoría
 * @returns {string} Estado formateado
 */
export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1);
};

/**
 * Convertir estado a booleano
 * @param {string} estado - Estado como string
 * @returns {boolean} Estado booleano
 */
export const convertirEstadoABoolean = (estado) => {
  return estado?.toLowerCase() === 'activo';
};

/**
 * Formatear descripción (cortar si es muy larga)
 * @param {string} descripcion - Descripción de la categoría
 * @param {number} maxLength - Longitud máxima
 * @returns {string} Descripción formateada
 */
export const formatearDescripcion = (descripcion, maxLength = 100) => {
  if (!descripcion) return 'Sin descripción';
  if (descripcion.length <= maxLength) return descripcion;
  return descripcion.substring(0, maxLength) + '...';
};

/**
 * Formatear margen
 * @param {number} margen - Margen de ganancia
 * @returns {string} Margen formateado
 */
export const formatearMargen = (margen) => {
  if (margen === undefined || margen === null) return 'N/A';
  return `${margen}%`;
};

// ==================== FUNCIONES DE EXPORTACIÓN ====================

/**
 * Exportar categorías a Excel
 * @param {Array} categorias - Lista de categorías
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @returns {Object} Resultado de la exportación
 */
export const exportarExcel = (categorias, filename = 'categorias') => {
  try {
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    // Importar dinámicamente para evitar problemas de SSR
    const XLSX = require('xlsx');
    
    // Crear un nuevo libro de trabajo
    const wb = XLSX.utils.book_new();
    
    // Preparar los datos
    const data = categorias.map(categoria => ({
      'ID': categoria.idCategoria || '',
      'Nombre': categoria.nombre || '',
      'Descripción': categoria.descripcion || '',
      'Margen %': categoria.margen_ganancia_default || 0,
      'Estado': convertirEstadoATexto(categoria.estado),
      'Fecha Creación': formatearFechaCategoria(categoria.fecha_creacion || categoria.fechaCreacion, true)
    }));
    
    // Convertir a hoja de cálculo
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Establecer anchos de columna
    const wscols = [
      { wch: 8 },   // ID
      { wch: 30 },  // Nombre
      { wch: 50 },  // Descripción
      { wch: 12 },  // Margen
      { wch: 15 },  // Estado
      { wch: 20 }   // Fecha Creación
    ];
    ws['!cols'] = wscols;
    
    // Agregar la hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, 'Categorías');
    
    // Generar nombre de archivo con fecha
    const fecha = new Date();
    const fechaStr = fecha.toISOString().split('T')[0];
    const nombreArchivo = `${filename}_${fechaStr}.xlsx`;
    
    // Guardar el archivo
    XLSX.writeFile(wb, nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: `Archivo ${nombreArchivo} generado exitosamente`,
      count: categorias.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando a Excel:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar categorías a PDF
 * @param {Array} categorias - Lista de categorías
 * @param {string} titulo - Título del reporte
 * @param {string} logoBase64 - Logo en base64 (opcional)
 * @returns {Object} Resultado de la exportación
 */
export const exportarPDF = (categorias, titulo = 'Reporte de Categorías', logoBase64 = null) => {
  try {
    console.log('📊 Exportando PDF de categorías...');
    
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (!categorias || categorias.length === 0) {
      throw new Error('No hay categorías para exportar');
    }
    
    // Usar jsPDF y autoTable
    let jsPDF;
    try {
      const jspdf = require('jspdf');
      jsPDF = jspdf.jsPDF;
      require('jspdf-autotable');
    } catch (e) {
      console.error('❌ jsPDF no está disponible:', e);
      return {
        success: false,
        error: 'La librería jsPDF no está disponible',
        filename: null
      };
    }
    
    const doc = new jsPDF();
    const fecha = new Date();
    const fechaStr = fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    let y = 20;
    const marginLeft = 20;
    const pageWidth = doc.internal.pageSize.width;
    
    // Agregar logo si se proporcionó
    let logoAgregado = false;
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'JPEG', marginLeft, 10, 30, 15);
        logoAgregado = true;
        y = 35;
      } catch (logoError) {
        console.warn('⚠️ Error al agregar logo:', logoError);
        y = 20;
      }
    }
    
    // Título
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const titleX = logoAgregado ? 60 : marginLeft;
    doc.text('JEAN INFORMATICO C.A.', titleX, y);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(titulo, titleX, y + 8);
    
    // Información
    y += 20;
    doc.setFontSize(10);
    doc.text(`Fecha: ${fechaStr}`, marginLeft, y);
    doc.text(`Total: ${categorias.length} categorías`, marginLeft + 80, y);
    
    // Estadísticas
    y += 15;
    const estadisticas = obtenerEstadisticasCategorias(categorias);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen Estadístico', marginLeft, y);
    
    y += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    const resumen = [
      `Categorías activas: ${estadisticas.activas} (${estadisticas.porcentajeActivas}%)`,
      `Categorías inactivas: ${estadisticas.inactivas} (${estadisticas.porcentajeInactivas}%)`,
      `Con descripción: ${estadisticas.conDescripcion} (${estadisticas.porcentajeConDescripcion}%)`,
      `Promedio margen: ${estadisticas.promedioMargen}%`
    ];
    
    resumen.forEach((linea, index) => {
      doc.text(`• ${linea}`, marginLeft + 5, y + (index * 5));
    });
    
    // Preparar datos para la tabla
    const tableData = categorias.map(categoria => [
      categoria.idCategoria || '',
      categoria.nombre || '',
      formatearDescripcion(categoria.descripcion, 30) || '',
      `${categoria.margen_ganancia_default || 0}%`,
      convertirEstadoATexto(categoria.estado),
      formatearFechaCategoria(categoria.fecha_creacion || categoria.fechaCreacion)
    ]);
    
    // Crear tabla
    doc.autoTable({
      startY: y + 25,
      head: [['ID', 'Nombre', 'Descripción', 'Margen', 'Estado', 'Fecha']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [79, 70, 229],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 40 },
        2: { cellWidth: 45 },
        3: { cellWidth: 20 },
        4: { cellWidth: 20 },
        5: { cellWidth: 30 }
      },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak'
      },
      didDrawPage: function(data) {
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text('© Jean Informatico C.A. - Sistema de Gestión de Categorías', marginLeft, 290);
        doc.text(`Página ${data.pageNumber}`, pageWidth - marginLeft, 290, null, null, 'right');
      }
    });
    
    // Guardar archivo
    const fechaISO = fecha.toISOString().split('T')[0];
    const nombreArchivo = `categorias_${fechaISO}.pdf`;
    doc.save(nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: 'PDF generado exitosamente',
      count: categorias.length,
      withLogo: logoAgregado
    };
    
  } catch (error) {
    console.error('❌ Error exportando PDF:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar reporte completo (Excel + PDF)
 * @param {Array} categorias - Lista de categorías
 * @param {string} baseFilename - Nombre base del archivo
 * @param {string} logoBase64 - Logo en base64 (opcional)
 * @returns {Promise} Resultado de la exportación
 */
export const exportarReporteCompleto = async (categorias, baseFilename = 'reporte_completo_categorias', logoBase64 = null) => {
  try {
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (categorias.length === 0) {
      throw new Error('No hay datos para exportar');
    }
    
    // Exportar Excel
    const excelResult = exportarExcel(categorias, baseFilename);
    if (!excelResult.success) {
      throw new Error(`Error en Excel: ${excelResult.error}`);
    }
    
    // Pequeño delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Exportar PDF
    const pdfResult = exportarPDF(categorias, `Reporte de Categorías - ${baseFilename}`, logoBase64);
    if (!pdfResult.success) {
      throw new Error(`Error en PDF: ${pdfResult.error}`);
    }
    
    return {
      success: true,
      excel: excelResult,
      pdf: pdfResult,
      message: 'Reporte completo generado exitosamente',
      count: categorias.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando reporte completo:', error);
    return {
      success: false,
      error: error.message,
      excel: null,
      pdf: null
    };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Funciones API
  obtenerCategorias,
  obtenerCategoriaPorId,
  obtenerCategoriaConRelaciones,
  buscarCategoriasAPI,
  verificarCategoriaPorNombre,
  verificarCategoriaPorId,
  obtenerCategoriasPorMargen,
  crearCategoria,
  actualizarCategoria,
  eliminarCategoria,
  activarCategoria,
  desactivarCategoria,
  cambiarEstadoCategoria,
  
  // Funciones auxiliares
  formatearFechaCategoria,
  convertirEstadoATexto,
  convertirEstadoABoolean,
  formatearDescripcion,
  formatearMargen,
  buscarCategoriasLocal,
  filtrarCategoriasPorEstadoLocal,
  obtenerEstadisticasCategorias,
  ordenarCategorias,
  
  // Funciones de exportación
  exportarExcel,
  exportarPDF,
  exportarReporteCompleto
};